import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import LabelWithInputField from "./LabelWithInputField";
import ListWrapper from "../utils/ListWrapper";
import ResultStatus from "../classes/ResultStatus";
import SuccessMessage from "../classes/SuccessMessage";
import CheckServerErrors from "./CheckServerErrors";
import Administrator from "../models/Administrator";
import EventBus from "../utils/EventBus";
import TextView from "./TextView";
import Result from "../classes/Result";
import RPCServices from "../rpc/RPCServices";
import CollectionUtils from "../utils/CollectionUtils";
import { Runnable } from "../classes/core";
import { BuildContext } from "../classes/BuildContext";

type _UpdateButtonOnPressed = (d3eState: AdminProfileViewRefs) => void;

type _LabelwithInputField2OnChanged = (
  text: string,
  d3eState: AdminProfileViewRefs
) => void;

type _LabelwithInputField8OnChanged = (
  text: string,
  d3eState: AdminProfileViewRefs
) => void;

export interface AdminProfileViewProps extends BaseUIProps {
  key?: string;
  admin: Administrator;
}
/// To store state data for AdminProfileView
class AdminProfileViewRefs {
  public updateButton: UpdateButtonState = new UpdateButtonState();
}

interface UpdateButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminProfileViewRefs;
  _updateButtonHandler?: _UpdateButtonOnPressed;
}

class UpdateButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _UpdateButtonWithState extends ObservableComponent<UpdateButtonWithStateProps> {
  updateButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: UpdateButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get updateButton(): UpdateButtonState {
    return this.props.d3eState.updateButton;
  }
  public get d3eState(): AdminProfileViewRefs {
    return this.props.d3eState;
  }
  public get _updateButtonHandler(): _UpdateButtonOnPressed {
    return this.props._updateButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("updateButton", null, this.updateButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["updateButton", "updateButton.", "updateButton.hover"],
      this.rebuild
    );
  }
  public updateButtonOnEnter(event): void {
    return this.updateButton.setHover(true);
  }
  public updateButtonOnExit(event): void {
    return this.updateButton.setHover(false);
  }
  public dispose(): void {
    this.updateButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      width: 120,
      child: Button({
        padding: this.updateButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.updateButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.updateButton.disable,
        onPressed: () => {
          this._updateButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Update" }),
        onEnter: (event) => {
          this.updateButtonOnEnter(event);
        },
        onExit: (event) => {
          this.updateButtonOnExit(event);
        },
      }),
      className: "x83 hc",
    });
  }
}
function UpdateButtonWithState(props: UpdateButtonWithStateProps) {
  return React.createElement(_UpdateButtonWithState, props);
}

class _AdminProfileViewState extends ObservableComponent<AdminProfileViewProps> {
  static defaultProps = { admin: null };
  d3eState: AdminProfileViewRefs = new AdminProfileViewRefs();
  errors: Array<string> = ListWrapper.widget(this, "errors");
  private _editInitListeners: Array<Runnable> = [];
  private _d3eModelErrors: Array<string> = [];
  public emailUniqueError: string;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminProfileViewProps) {
    super(props);

    this.initState();
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("admin", this.props.admin);

    this.on(
      ["admin", "admin.email", "admin.mobileNumber", "admin.name", "errors"],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AdminProfileViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }
  }
  public setErrors(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.errors, val);

    if (!isValChanged) {
      return;
    }

    this.errors.clear();

    this.errors.addAll(val);

    this.fire("errors", this);
  }
  public addToErrors(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.errors.contains(val)) this.errors.add(val);
    } else {
      this.errors.remove(this.errors.elementAt(index));

      this.errors.add(val);
    }

    this.fire("errors", this, val, true);
  }
  public removeFromErrors(val: string): void {
    this.errors.remove(val);

    this.fire("errors", this, val, false);
  }
  public get d3eModelErrors(): Array<string> {
    return this._d3eModelErrors ?? [];
  }
  public set d3eModelErrors(val: Array<string>) {
    this._d3eModelErrors.clear();

    this._d3eModelErrors.addAll(val);
  }
  public async checkEmailUniqueInAdministrator(): Promise<void> {
    if (this.admin == null) {
      return;
    }

    let isUnique: boolean =
      await RPCServices.getUniqueChecker().checkEmailUniqueInAdministrator(
        this.admin,
        this.admin.email
      );

    if (!isUnique) {
      this.emailUniqueError = "Duplicate email found with this value.";
    }
  }
  public setEditDefaults(): void {}
  public get isEditValid(): boolean {
    return true;
  }
  public validate(): Array<string> {
    let errors: Array<string> = [];

    return errors;
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                ui.Container({
                  margin: ui.EdgeInsets.symmetric({
                    horizontal: 0.0,
                    vertical: 10.0,
                    transitions: new Map(),
                  }),
                  width: 400,
                  child: LabelWithInputField({
                    name: "Name",
                    placeHolder: "Enter Name",
                    isRequired: true,
                    value: this.admin.name,
                    errors: this.admin.name === "" ? ["Name is required"] : [],
                    onChanged: (text) => {
                      this.labelwithInputField2onChanged(text, this.d3eState);
                    },
                  }),
                  key: "0",
                  className: "x03 hc",
                }),
                ui.Container({
                  width: 400,
                  child: LabelWithInputField({
                    placeHolder: "Enter Email ID",
                    isRequired: true,
                    name: "Email ID",
                    disable: true,
                    value: this.admin.email,
                  }),
                  key: "1",
                  className: "x1ce hc",
                }),
                ui.Container({
                  margin: ui.EdgeInsets.symmetric({
                    horizontal: 0.0,
                    vertical: 10.0,
                    transitions: new Map(),
                  }),
                  padding: ui.EdgeInsets.fromLTRB(
                    0.0,
                    0.0,
                    0.0,
                    5.0,
                    new Map()
                  ),
                  width: 400,
                  child: LabelWithInputField({
                    placeHolder: "Enter Mobile Number",
                    name: "Mobile Number",
                    value: this.admin.mobileNumber,
                    onChanged: (text) => {
                      this.labelwithInputField8onChanged(text, this.d3eState);
                    },
                  }),
                  key: "2",
                  className: "x99 hc",
                }),
              ],
              className: "xc1e hc",
              key: "0",
            }),
            ui.Column({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                this.errors.isNotEmpty
                  ? CheckServerErrors({ errors: this.errors, className: "hc" })
                  : [],
                ui.Row({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    UpdateButtonWithState({
                      d3eState: this.d3eState,
                      _updateButtonHandler: this.updateButtonHandler,
                      key: "0",
                    }),
                  ],
                  className: "x8be hc h",
                  key: "1",
                }),
              ],
              className: "x38 hc h",
              key: "1",
            }),
          ],
          className: "x3cb hc h",
          key: "0",
        }),
      ],
      className: ui.join(
        this.props.className,
        "AdminProfileView xad hc vc h v"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onDispose = (): void => {
    this.admin.restore();
  };
  public updateButtonHandler = async (
    d3eState: AdminProfileViewRefs
  ): Promise<void> => {
    let res: Result<Administrator> = await this.admin.save();

    if (res.status === ResultStatus.Success) {
      EventBus.get().fire(
        new SuccessMessage({ message: "Successfully saved" })
      );
    } else {
      this.errors.clear();

      this.errors.addAll(res.errors);
    }
  };
  public dispose(): void {
    super.dispose();

    this.onDispose();
  }
  public labelwithInputField2onChanged = (
    val: string,
    d3eState: AdminProfileViewRefs
  ): void => {
    this.admin.setName(val);
  };
  public labelwithInputField8onChanged = (
    val: string,
    d3eState: AdminProfileViewRefs
  ): void => {
    this.admin.setMobileNumber(val);
  };
  public get updateButton() {
    return this.d3eState.updateButton;
  }
}
export default function AdminProfileView(props: AdminProfileViewProps) {
  return React.createElement(_AdminProfileViewState, {
    ..._AdminProfileViewState.defaultProps,
    ...props,
  });
}
